import React, { useState, useEffect } from 'react';
import { Platform, isComposioPlatform, COMPOSIO_PLATFORMS } from '../constants/platforms';
import { generatePostImage, regenerateSinglePost } from '../services/geminiService';
import { overlayLogoOnImage } from '../services/imageUtils';
import { toast } from 'sonner';
import { 
  Facebook, Instagram, Linkedin, Twitter, Video, Loader2, Share2, Sparkles, Send, 
  Pencil, X, Check, TrendingUp, Copy, Eye, MoreHorizontal, Heart, MessageCircle, 
  Link2, PlusCircle, MinusCircle, ExternalLink, Wand2, Lock, Cloud, Link, Clock,
  CalendarClock, Zap, Upload
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Utility to display shortened URL
const displayUrl = (url) => {
  try {
    const urlObj = new URL(url);
    const domain = urlObj.hostname.replace('www.', '');
    const path = urlObj.pathname;
    // Show domain + shortened path
    if (path.length > 20) {
      return `${domain}${path.substring(0, 15)}...`;
    }
    return `${domain}${path}`.substring(0, 35) + (url.length > 40 ? '...' : '');
  } catch {
    return url.substring(0, 35) + (url.length > 35 ? '...' : '');
  }
};

// Utility to shorten URLs via backend API
const shortenUrl = async (url, title, userId = null) => {
  try {
    const response = await fetch(`${BACKEND_URL}/api/shorten`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ url, title, user_id: userId })
    });
    if (response.ok) {
      const data = await response.json();
      // Backend now returns full short_url with custom domain if configured
      return data;
    }
  } catch (err) {
    console.error('Failed to shorten URL:', err);
  }
  return null;
};

// Best times to post by platform (based on industry research)
const BEST_POSTING_TIMES = {
  'instagram': {
    days: ['Monday', 'Tuesday', 'Wednesday'],
    times: ['11 AM - 1 PM', '7 PM - 9 PM'],
    peak: 'Wednesday 11 AM',
    tip: 'Reels perform best in evening hours'
  },
  'twitter': {
    days: ['Tuesday', 'Wednesday', 'Thursday'],
    times: ['9 AM - 12 PM', '5 PM - 6 PM'],
    peak: 'Wednesday 9 AM',
    tip: 'Tweets have short lifespan - post multiple times'
  },
  'linkedin': {
    days: ['Tuesday', 'Wednesday', 'Thursday'],
    times: ['7 AM - 8 AM', '12 PM', '5 PM - 6 PM'],
    peak: 'Tuesday & Wednesday 10 AM',
    tip: 'B2B content peaks during work hours'
  },
  'facebook': {
    days: ['Tuesday', 'Wednesday', 'Friday'],
    times: ['9 AM - 1 PM', '4 PM - 5 PM'],
    peak: 'Wednesday 11 AM',
    tip: 'Video content gets 10x more engagement'
  },
  'tiktok': {
    days: ['Tuesday', 'Thursday', 'Friday'],
    times: ['10 AM - 12 PM', '7 PM - 9 PM'],
    peak: 'Thursday 7 PM',
    tip: 'Post when your audience is scrolling (lunch & evening)'
  },
  'bluesky': {
    days: ['Monday', 'Wednesday', 'Friday'],
    times: ['9 AM - 11 AM', '5 PM - 7 PM'],
    peak: 'Weekdays 10 AM',
    tip: 'Engage in threads for better visibility'
  }
};

// Helper to get best posting times (case-insensitive)
const getBestTimes = (platform) => {
  return BEST_POSTING_TIMES[platform?.toLowerCase()] || null;
};

const PlatformIcon = ({ platform }) => {
  const p = platform?.toLowerCase();
  switch (p) {
    case 'facebook': return <Facebook className="w-4 h-4 text-blue-500" />;
    case 'instagram': return <Instagram className="w-4 h-4 text-pink-500" />;
    case 'linkedin': return <Linkedin className="w-4 h-4 text-blue-700" />;
    case 'tiktok': return <Video className="w-4 h-4 text-black dark:text-white" />;
    case 'twitter': 
    case 'x': return <Twitter className="w-4 h-4 text-sky-500" />;
    case 'bluesky': return <Cloud className="w-4 h-4 text-sky-400" />;
    default: return <Share2 className="w-4 h-4 text-gray-500" />;
  }
};

const PreviewCard = ({ post, logo, businessName }) => {
  // Ensure hashtags is always an array
  const getHashtags = () => {
    if (Array.isArray(post.hashtags)) return post.hashtags;
    if (typeof post.hashtags === 'string') return post.hashtags.split(/[,\s]+/).filter(Boolean);
    return [];
  };

  return (
    <div className="bg-white text-gray-900 rounded-lg overflow-hidden w-full mx-auto font-sans shadow-xl">
      <div className="p-3 flex items-center gap-2 border-b border-gray-100">
        <div className="w-8 h-8 rounded-full bg-gray-200 overflow-hidden flex-shrink-0 border border-gray-100">
          {logo ? <img src={logo} alt="Logo" className="w-full h-full object-cover" /> : <div className="w-full h-full bg-indigo-600 flex items-center justify-center text-white text-xs font-bold">{businessName?.[0] || 'A'}</div>}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold truncate">{businessName || 'Your Business'}</p>
          <p className="text-xs text-gray-500 flex items-center gap-1">Sponsored • <PlatformIcon platform={post.platform} /></p>
        </div>
        <MoreHorizontal className="w-4 h-4 text-gray-400" />
      </div>

      <div className="p-3 pb-2">
        <p className="text-sm whitespace-pre-wrap leading-relaxed">{post.content}</p>
        <div className="mt-2 text-sm text-blue-600 font-medium">
          {getHashtags().map(t => t.startsWith('#') ? t : `#${t}`).join(' ')}
        </div>
      </div>

      {post.imageUrl && (
        <div className="w-full bg-gray-100">
          <img src={post.imageUrl} alt="Post content" className="w-full h-auto block" />
        </div>
      )}
      
      {post.videoUrl && (
        <div className="w-full aspect-[9/16] bg-black flex items-center justify-center relative">
          <Video className="w-12 h-12 text-white opacity-50" />
          <div className="absolute bottom-4 left-4 right-4 text-white">
            <p className="text-sm font-bold shadow-black drop-shadow-md">{post.content.slice(0, 50)}...</p>
          </div>
        </div>
      )}

      {/* Mock Engagement Stats */}
      <div className="px-3 py-2 flex justify-between items-center text-xs text-gray-500 border-b border-gray-50">
        <span>1.2K Likes</span>
        <span>45 Comments</span>
      </div>

      {/* Mock Actions */}
      <div className="grid grid-cols-3 gap-1 p-1">
        <button className="flex items-center justify-center gap-1 py-2 hover:bg-gray-50 rounded text-gray-600 font-medium text-xs">
          <Heart className="w-4 h-4" /> Like
        </button>
        <button className="flex items-center justify-center gap-1 py-2 hover:bg-gray-50 rounded text-gray-600 font-medium text-xs">
          <MessageCircle className="w-4 h-4" /> Comment
        </button>
        <button className="flex items-center justify-center gap-1 py-2 hover:bg-gray-50 rounded text-gray-600 font-medium text-xs">
          <Share2 className="w-4 h-4" /> Share
        </button>
      </div>
    </div>
  );
};

const PostCard = ({ post, onUpdate, onPublish, profile, userCredits, onNeedCredits, automationSettings, connectedAccounts, userId, onBlueSkyPost, onComposioPublish }) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [isEditingPrompt, setIsEditingPrompt] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [showBestTimes, setShowBestTimes] = useState(false);
  
  // Regeneration State
  const [showRegenerateInput, setShowRegenerateInput] = useState(false);
  const [regenerationInstruction, setRegenerationInstruction] = useState('');
  const [isRegenerating, setIsRegenerating] = useState(false);
  
  // URL shortening state
  const [shortenedUrls, setShortenedUrls] = useState({});
  const [shorteningUrl, setShorteningUrl] = useState(null);
  
  // BlueSky posting state
  const [isPostingToBlueSky, setIsPostingToBlueSky] = useState(false);
  const [showBlueSkyConfirm, setShowBlueSkyConfirm] = useState(false);
  const [blueSkyPostResult, setBlueSkyPostResult] = useState(null);

  // Composio direct publishing state
  const [isPublishingComposio, setIsPublishingComposio] = useState(false);
  const [showComposioConfirm, setShowComposioConfirm] = useState(false);
  const [composioPublishResult, setComposioPublishResult] = useState(null);

  // Check if BlueSky post button should be shown
  const isBlueSkyPost = post.platform?.toLowerCase() === 'bluesky';
  const canPostToBlueSky = isBlueSkyPost && automationSettings?.bluesky_connected;

  // Check if this platform supports Composio direct publishing
  const platformLower = post.platform?.toLowerCase();
  const isComposioPlatformPost = COMPOSIO_PLATFORMS.includes(platformLower);
  
  // Check if the user has a connected account for this platform
  const hasConnectedAccount = connectedAccounts?.some(
    account => account.platform?.toLowerCase() === platformLower && account.connected
  );
  
  // Can publish directly to Composio
  const canPublishComposio = isComposioPlatformPost && hasConnectedAccount && !post.status?.includes('posted');

  // Handle direct publishing to Composio platforms
  const handlePublishComposio = async () => {
    if (!userId || !canPublishComposio) return;
    
    setIsPublishingComposio(true);
    setComposioPublishResult(null);
    
    try {
      // Construct the full post text with hashtags
      const hashtags = getHashtags().map(t => t.startsWith('#') ? t : `#${t}`).join(' ');
      const fullText = `${post.content}\n\n${hashtags}`;
      
      const response = await fetch(`${BACKEND_URL}/api/social/posts/${post.id}/publish`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          text: fullText,
          image_url: post.imageUrl || null,
          platform: post.platform  // Include platform for inline publishing
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        const platformResult = data.publish_results?.[platformLower];
        
        if (platformResult?.ok) {
          setComposioPublishResult({ success: true, url: platformResult.url });
          toast.success(`Successfully posted to ${post.platform}!`);
          
          // Update the post status
          onUpdate({
            ...post,
            status: 'posted',
            posted_at: new Date().toISOString(),
            post_url: platformResult.url || null,
            publish_results: data.publish_results
          });
          
          if (onComposioPublish) {
            onComposioPublish();
          }
        } else {
          const errorMsg = platformResult?.error || 'Failed to publish';
          setComposioPublishResult({ success: false, error: errorMsg });
          toast.error(`Publishing failed: ${errorMsg}`);
        }
      } else {
        const errorMsg = data.detail || 'Failed to publish';
        setComposioPublishResult({ success: false, error: errorMsg });
        toast.error(`Publishing failed: ${errorMsg}`);
      }
    } catch (err) {
      const errorMsg = err.message || 'Network error';
      setComposioPublishResult({ success: false, error: errorMsg });
      toast.error(`Publishing failed: ${errorMsg}`);
    } finally {
      setIsPublishingComposio(false);
      setShowComposioConfirm(false);
    }
  };

  // Handle posting to BlueSky
  const handlePostToBlueSky = async () => {
    if (!userId || !automationSettings?.bluesky_connected) return;
    
    setIsPostingToBlueSky(true);
    setBlueSkyPostResult(null);
    
    try {
      // Construct the full post text with hashtags
      const hashtags = getHashtags().map(t => t.startsWith('#') ? t : `#${t}`).join(' ');
      const fullText = `${post.content}\n\n${hashtags}`;
      
      const response = await fetch(`${BACKEND_URL}/api/automation/bluesky/post`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          text: fullText,
          image_url: post.imageUrl || null
        })
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        setBlueSkyPostResult({ success: true, url: data.post_url });
        // Update the post with the BlueSky URL and mark as posted
        onUpdate({
          ...post,
          status: 'posted',
          posted_at: new Date().toISOString(),
          post_url: data.post_url
        });
        if (onBlueSkyPost) {
          onBlueSkyPost(post, data.post_url);
        }
      } else {
        setBlueSkyPostResult({ success: false, error: data.error || 'Failed to post' });
      }
    } catch (err) {
      setBlueSkyPostResult({ success: false, error: err.message || 'Network error' });
    } finally {
      setIsPostingToBlueSky(false);
      setShowBlueSkyConfirm(false);
    }
  };

  // Check if user can regenerate content
  const canRegenerateContent = () => {
    if (!userCredits) return true; // Allow if no credits system (free tier)
    if (userCredits.content_regens_remaining === -1) return true; // Unlimited
    return userCredits.content_regens_remaining > 0;
  };

  // Check if user can regenerate images
  const canRegenerateImage = () => {
    if (!userCredits) return true; // Allow if no credits system (free tier)
    if (userCredits.image_regens_remaining === -1) return true; // Unlimited
    return userCredits.image_regens_remaining > 0;
  };

  // Ensure hashtags is always an array
  const getHashtags = () => {
    if (Array.isArray(post.hashtags)) return post.hashtags;
    if (typeof post.hashtags === 'string') return post.hashtags.split(/[,\s]+/).filter(Boolean);
    return [];
  };

  const handleGenerateImage = async () => {
    if (!post.imagePrompt) return;
    
    // Check image regen credits
    if (!canRegenerateImage()) {
      onNeedCredits?.('AI Image Generation', 'image regeneration credits');
      return;
    }
    
    setIsGenerating(true);
    try {
      let finalImage = await generatePostImage(post.imagePrompt);
      if (profile?.logo) {
        try {
          finalImage = await overlayLogoOnImage(finalImage, profile.logo);
        } catch (e) {
          console.error("Failed to overlay logo", e);
        }
      }
      onUpdate({ ...post, imageUrl: finalImage });
    } catch (error) {
      alert("Failed to generate image.");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleRegeneratePost = async () => {
    if (!regenerationInstruction.trim() || !profile) return;
    
    // Check content regen credits
    if (!canRegenerateContent()) {
      onNeedCredits?.('AI Content Regeneration', 'content regeneration credits');
      return;
    }
    
    setIsRegenerating(true);
    try {
      const updatedData = await regenerateSinglePost(post, regenerationInstruction, profile);
      onUpdate({
        ...post,
        content: updatedData.content || post.content,
        hashtags: updatedData.hashtags || post.hashtags,
        imagePrompt: updatedData.imagePrompt || post.imagePrompt,
        engagementScore: updatedData.engagementScore || post.engagementScore,
        engagementReasoning: updatedData.engagementReasoning || post.engagementReasoning
      });
      setShowRegenerateInput(false);
      setRegenerationInstruction('');
    } catch (error) {
      alert("Failed to regenerate post.");
    } finally {
      setIsRegenerating(false);
    }
  };

  const handleCopyVideoPrompt = () => {
    navigator.clipboard.writeText(post.imagePrompt);
    alert("Video prompt copied to clipboard! You can paste this into any AI video tool.");
  };

  const toggleSourceInContent = async (source) => {
    // Check if URL is already in content (either original or shortened)
    const hasOriginalUrl = post.content.includes(source.url);
    const shortCode = shortenedUrls[source.url];
    const hasShortUrl = shortCode && post.content.includes(`flwgn.link/${shortCode}`);
    
    if (hasOriginalUrl || hasShortUrl) {
      // Remove the citation
      let newContent = post.content;
      
      // Try to remove with short URL first
      if (shortCode) {
        const shortUrlPattern = new RegExp(`\\n?\\n?🔗[^\\n]*flwgn\\.link[^\\n]*${shortCode}[^\\n]*`, 'g');
        newContent = newContent.replace(shortUrlPattern, '');
      }
      
      // Also try to remove with original URL
      const originalUrlPattern = new RegExp(`\\n?\\n?🔗[^\\n]*${source.url.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}[^\\n]*`, 'g');
      newContent = newContent.replace(originalUrlPattern, '');
      
      onUpdate({ ...post, content: newContent.trim() });
    } else {
      // Add the citation with shortened URL
      setShorteningUrl(source.url);
      
      try {
        // Get or create short URL (pass userId for tracking)
        const shortened = await shortenUrl(source.url, source.title, userId);
        
        if (shortened && shortened.short_url) {
          // Store the short code for future reference
          setShortenedUrls(prev => ({
            ...prev,
            [source.url]: shortened.short_code
          }));
          
          // Add citation with short URL
          const citation = `\n\n🔗 ${source.title}: ${shortened.short_url}`;
          onUpdate({ ...post, content: (post.content + citation).trim() });
        } else {
          // Fallback to original URL if shortening fails
          const citation = `\n\n🔗 ${source.title}: ${source.url}`;
          onUpdate({ ...post, content: (post.content + citation).trim() });
        }
      } catch (err) {
        console.error('Failed to shorten URL:', err);
        // Fallback to original URL
        const citation = `\n\n🔗 ${source.title}: ${source.url}`;
        onUpdate({ ...post, content: (post.content + citation).trim() });
      } finally {
        setShorteningUrl(null);
      }
    }
  };

  const handleQuickShare = async () => {
    const fullText = `${post.content}\n\n${getHashtags().map(t => t.startsWith('#') ? t : `#${t}`).join(' ')}`;
    
    // Try native Web Share API first (works on mobile and some desktop browsers)
    if (navigator.share) {
      try {
        const shareData = {
          title: `${post.platform} Post`,
          text: fullText,
        };
        
        // Try to include image if available
        if (post.imageUrl && navigator.canShare) {
          try {
            const response = await fetch(post.imageUrl);
            const blob = await response.blob();
            const file = new File([blob], 'post-image.png', { type: blob.type });
            if (navigator.canShare({ files: [file] })) {
              shareData.files = [file];
            }
          } catch (e) {
            console.log('Could not share image:', e);
          }
        }
        
        await navigator.share(shareData);
        return;
      } catch (err) {
        if (err.name === 'AbortError') return; // User cancelled
        console.log('Native share not available, falling back to platform links');
      }
    }

    // Fallback: Copy text and open platform
    const encodedText = encodeURIComponent(fullText);
    let url = '';
    let shouldCopy = false;

    switch (post.platform) {
      case Platform.Twitter: url = `https://twitter.com/intent/tweet?text=${encodedText}`; break;
      case Platform.LinkedIn: url = `https://www.linkedin.com/feed/`; shouldCopy = true; break;
      case Platform.Facebook: url = `https://www.facebook.com/`; shouldCopy = true; break;
      case Platform.Instagram: url = `https://www.instagram.com/`; shouldCopy = true; break;
      case Platform.TikTok: url = `https://www.tiktok.com/upload`; shouldCopy = true; break;
      case Platform.BlueSky: url = `https://bsky.app/`; shouldCopy = true; break;
      default: url = 'https://google.com';
    }

    if (shouldCopy) {
      try {
        await navigator.clipboard.writeText(fullText);
        alert(`Caption copied! Opening ${post.platform}...`);
      } catch (e) {
        console.error('Failed to copy text', e);
      }
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const getScoreColor = (score) => {
    if (score >= 85) return 'text-green-400 bg-green-900/20 border-green-800';
    if (score >= 70) return 'text-brand-orange bg-brand-orange/10 border-brand-orange/30';
    return 'text-amber-400 bg-amber-900/20 border-amber-800';
  };

  return (
    <>
      <div className="bg-brand-gray-900 border border-brand-gray-700 rounded-xl overflow-hidden shadow-lg flex flex-col h-full group hover:border-brand-orange/30 transition-colors" data-testid="post-card">
        <div className="p-4 border-b border-brand-gray-700 flex justify-between items-center bg-brand-gray-800/50">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-brand-gray-700 rounded-lg"><PlatformIcon platform={post.platform} /></div>
            <div>
              <p className="text-sm font-semibold text-white">{post.platform}</p>
              <p className="text-xs text-brand-gray-400">{post.date} • {post.time || '10:00 AM'}</p>
            </div>
          </div>
          {post.engagementScore && (
            <div className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold border ${getScoreColor(post.engagementScore)}`}>
              <TrendingUp className="w-3 h-3" /> {post.engagementScore}% POTENTIAL
            </div>
          )}
        </div>

        <div className="p-4 flex-grow flex flex-col gap-4">
          <div className="space-y-2 group/edit relative">
            <textarea 
              value={post.content}
              onChange={(e) => onUpdate({...post, content: e.target.value})}
              data-testid="post-content-textarea"
              className="w-full bg-black/50 text-sm text-white p-3 rounded-lg border border-brand-gray-700 focus:border-brand-orange focus:ring-1 focus:ring-brand-orange outline-none resize-y min-h-[120px] transition-all leading-relaxed whitespace-pre-wrap"
              spellCheck={false}
            />
            <div className="flex flex-wrap gap-1 items-center justify-between">
              <div className="flex flex-wrap gap-1">
                {getHashtags().map((tag, idx) => <span key={idx} className="text-xs text-brand-orange">#{tag.replace('#', '')}</span>)}
              </div>
              
              {/* Best Times to Post Indicator */}
              {getBestTimes(post.platform) && (
                <button 
                  onClick={() => setShowBestTimes(true)}
                  className="flex items-center gap-1 px-2 py-1 text-[10px] text-brand-gray-400 hover:text-brand-orange bg-brand-gray-800 border border-brand-gray-700 hover:border-brand-orange/50 rounded-md transition-all"
                >
                  <Clock className="w-3 h-3" />
                  <span>Best Times</span>
                </button>
              )}
              
              {/* AI Edit Button */}
              <button 
                onClick={() => {
                  if (!canRegenerateContent()) {
                    onNeedCredits?.('AI Content Regeneration', 'content regeneration credits');
                    return;
                  }
                  setShowRegenerateInput(!showRegenerateInput);
                }}
                data-testid="ai-edit-btn"
                className={`flex items-center gap-1.5 px-2 py-1 text-[10px] font-bold uppercase bg-brand-gray-800 border rounded-md transition-all ${
                  canRegenerateContent()
                    ? 'text-brand-gray-400 hover:text-brand-orange border-brand-gray-700 hover:border-brand-orange'
                    : 'text-brand-gray-600 border-brand-gray-700 cursor-not-allowed'
                }`}
              >
                {canRegenerateContent() ? (
                  <Wand2 className="w-3 h-3" />
                ) : (
                  <Lock className="w-3 h-3" />
                )}
                AI Edit
                {userCredits && userCredits.content_regens_remaining !== -1 && userCredits.content_regens_remaining > 0 && (
                  <span className="ml-1 text-[8px] text-brand-gray-500">({userCredits.content_regens_remaining})</span>
                )}
              </button>
            </div>

            {/* AI Regeneration Input */}
            {showRegenerateInput && (
              <div className="mt-2 p-3 bg-brand-orange/10 rounded-lg border border-brand-orange/30">
                <p className="text-[10px] font-bold text-brand-orange mb-2 uppercase">How should I rewrite this?</p>
                <div className="flex gap-2">
                  <input 
                    type="text" 
                    value={regenerationInstruction}
                    onChange={(e) => setRegenerationInstruction(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleRegeneratePost()}
                    placeholder="e.g. Make it shorter, add more emojis, focus on the sale..."
                    data-testid="regenerate-instruction-input"
                    className="flex-grow bg-black border border-brand-gray-700 rounded px-3 py-2 text-xs text-white focus:border-brand-orange outline-none"
                    autoFocus
                  />
                  <button 
                    onClick={handleRegeneratePost} 
                    disabled={isRegenerating || !regenerationInstruction.trim()}
                    data-testid="regenerate-submit-btn"
                    className="px-3 py-2 bg-brand-orange hover:bg-brand-orange/90 text-black rounded text-xs font-bold disabled:opacity-50 flex items-center gap-1"
                  >
                    {isRegenerating ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Research Sources Section */}
          {post.sources && post.sources.length > 0 && (
            <div className="mt-2 p-3 bg-black/30 rounded-lg border border-brand-gray-700">
              <div className="flex items-center gap-2 mb-2 text-xs font-semibold text-brand-gray-400 uppercase tracking-wider">
                <Link2 className="w-3 h-3" /> Research Sources
              </div>
              <div className="space-y-2">
                {post.sources.map((source, idx) => {
                  const shortCode = shortenedUrls[source.url];
                  const isAdded = post.content.includes(source.url) || (shortCode && post.content.includes(`flwgn.link/${shortCode}`));
                  const isShortening = shorteningUrl === source.url;
                  
                  return (
                    <div key={idx} className="flex items-center justify-between gap-2 text-xs p-2 bg-brand-gray-800/50 rounded border border-brand-gray-700 hover:border-brand-gray-600 transition-colors">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 text-brand-gray-300 font-medium">
                          <span className="truncate">{source.title}</span>
                          <a href={source.url} target="_blank" rel="noopener noreferrer" className="text-brand-gray-500 hover:text-brand-orange shrink-0" title="Open Original">
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        </div>
                        <span className="text-brand-gray-500 text-[10px] truncate block" title={source.url}>
                          {displayUrl(source.url)}
                        </span>
                      </div>
                      {/* Add/Remove from Post Button */}
                      <button 
                        onClick={() => toggleSourceInContent(source)}
                        disabled={isShortening}
                        className={`p-1.5 rounded-md transition-colors ${
                          isShortening
                            ? 'text-brand-gray-500'
                            : isAdded 
                              ? 'text-red-400 hover:bg-red-900/30' 
                              : 'text-green-400 hover:bg-green-900/30'
                        }`}
                        title={isShortening ? "Shortening..." : isAdded ? "Remove from post" : "Add to post (auto-shortens)"}
                      >
                        {isShortening ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : isAdded ? (
                          <MinusCircle className="w-4 h-4" />
                        ) : (
                          <PlusCircle className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="mt-auto pt-2">
            {post.videoUrl ? (
              <div className="relative rounded-lg overflow-hidden aspect-[9/16] border border-brand-gray-700 bg-black">
                <video src={post.videoUrl} controls playsInline loop className="w-full h-full object-contain" />
                <button onClick={() => onUpdate({ ...post, videoUrl: undefined })} className="absolute top-2 right-2 p-1.5 bg-black/60 hover:bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity z-10"><X className="w-4 h-4" /></button>
              </div>
            ) : post.imageUrl ? (
              <div className="relative rounded-lg overflow-hidden aspect-video border border-brand-gray-700">
                <img src={post.imageUrl} alt="AI generated" className="w-full h-full object-cover" />
                <button onClick={() => onUpdate({ ...post, imageUrl: undefined })} className="absolute top-2 right-2 p-1.5 bg-black/60 hover:bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"><X className="w-4 h-4" /></button>
              </div>
            ) : (
              <div className="bg-black/50 rounded-lg border border-dashed border-brand-gray-700 p-4 min-h-[80px] flex flex-col justify-center items-center text-center group/image relative">
                
                {isEditingPrompt ? (
                  <div className="w-full flex flex-col gap-2">
                    <label className="text-[10px] font-bold text-brand-gray-400 uppercase tracking-wider">Visual Prompt</label>
                    <textarea 
                      value={post.imagePrompt}
                      onChange={(e) => onUpdate({...post, imagePrompt: e.target.value})}
                      className="w-full bg-brand-gray-800 text-xs text-white p-2 rounded border border-brand-orange/50 focus:border-brand-orange outline-none resize-none"
                      rows={3}
                      autoFocus
                    />
                    <button onClick={() => setIsEditingPrompt(false)} className="self-end px-3 py-1 bg-green-600/20 hover:bg-green-600/30 text-green-400 border border-green-600/50 rounded text-[10px] font-bold uppercase flex items-center gap-1 transition-colors">
                      <Check className="w-3 h-3" /> Done
                    </button>
                  </div>
                ) : (
                  <>
                    <button onClick={() => setIsEditingPrompt(true)} className="absolute top-2 right-2 p-1.5 text-brand-gray-500 hover:text-white bg-brand-gray-800/50 hover:bg-brand-orange rounded-md opacity-0 group-hover/image:opacity-100 transition-all z-10">
                      <Pencil className="w-3 h-3" />
                    </button>

                    <div onClick={() => setIsEditingPrompt(true)} className="cursor-pointer hover:bg-brand-gray-800/30 w-full rounded p-1 transition-colors">
                      <p className="text-[10px] text-brand-gray-500 mb-3 italic line-clamp-3 px-2 group-hover/image:text-brand-gray-300 transition-colors">
                        {post.imagePrompt}
                      </p>
                    </div>
                    
                    <div className="flex flex-col gap-2 w-full max-w-[200px]">
                      <button 
                        onClick={handleGenerateImage} 
                        disabled={isGenerating} 
                        data-testid="generate-image-btn"
                        className={`inline-flex items-center justify-center gap-2 px-3 py-1.5 text-[10px] font-bold uppercase border rounded transition-colors disabled:opacity-50 ${
                          canRegenerateImage()
                            ? 'text-brand-orange bg-brand-orange/10 hover:bg-brand-orange/20 border-brand-orange/30'
                            : 'text-brand-gray-500 bg-brand-gray-800/30 border-brand-gray-700 cursor-not-allowed'
                        }`}
                      >
                        {isGenerating ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : canRegenerateImage() ? (
                          <Sparkles className="w-3 h-3" />
                        ) : (
                          <Lock className="w-3 h-3" />
                        )}
                        Generate AI Image
                        {userCredits && userCredits.image_regens_remaining !== -1 && userCredits.image_regens_remaining > 0 && (
                          <span className="text-[8px] opacity-70">({userCredits.image_regens_remaining})</span>
                        )}
                      </button>
                      
                      {post.platform === Platform.TikTok && (
                        <button onClick={handleCopyVideoPrompt} className="inline-flex items-center justify-center gap-2 px-3 py-1.5 text-[10px] font-bold uppercase text-brand-gray-300 bg-brand-gray-800 hover:bg-brand-gray-700 border border-brand-gray-700 rounded transition-colors group/veo">
                          <Copy className="w-3 h-3" /> Copy Video Prompt
                        </button>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="p-4 bg-brand-gray-800 border-t border-brand-gray-700 flex flex-col gap-3">
          {/* Composio Publish Result */}
          {composioPublishResult && (
            <div className={`p-2 rounded-lg text-xs flex items-center gap-2 ${
              composioPublishResult.success 
                ? 'bg-green-900/30 text-green-400 border border-green-800/50'
                : 'bg-red-900/30 text-red-400 border border-red-800/50'
            }`}>
              {composioPublishResult.success ? (
                <>
                  <Check className="w-4 h-4" />
                  <span>Posted to {post.platform}!</span>
                  {composioPublishResult.url && (
                    <a href={composioPublishResult.url} target="_blank" rel="noopener noreferrer" className="ml-auto text-green-300 hover:underline">
                      View Post →
                    </a>
                  )}
                </>
              ) : (
                <>
                  <X className="w-4 h-4" />
                  <span>{composioPublishResult.error}</span>
                </>
              )}
            </div>
          )}

          {/* BlueSky Post Result */}
          {blueSkyPostResult && (
            <div className={`p-2 rounded-lg text-xs flex items-center gap-2 ${
              blueSkyPostResult.success 
                ? 'bg-green-900/30 text-green-400 border border-green-800/50'
                : 'bg-red-900/30 text-red-400 border border-red-800/50'
            }`}>
              {blueSkyPostResult.success ? (
                <>
                  <Check className="w-4 h-4" />
                  <span>Posted to BlueSky!</span>
                  {blueSkyPostResult.url && (
                    <a href={blueSkyPostResult.url} target="_blank" rel="noopener noreferrer" className="ml-auto text-green-300 hover:underline">
                      View Post →
                    </a>
                  )}
                </>
              ) : (
                <>
                  <X className="w-4 h-4" />
                  <span>{blueSkyPostResult.error}</span>
                </>
              )}
            </div>
          )}

          {/* Composio Direct Publish Button - For LinkedIn, Facebook, Instagram when connected */}
          {canPublishComposio && (
            <button 
              onClick={() => setShowComposioConfirm(true)}
              disabled={isPublishingComposio}
              data-testid="publish-composio-btn"
              className="w-full px-3 py-2 text-xs font-medium text-white bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 rounded-md shadow-lg shadow-indigo-900/30 flex items-center justify-center gap-2 disabled:opacity-50 transition-all"
            >
              {isPublishingComposio ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Upload className="w-4 h-4" />
              )}
              Publish to {post.platform}
            </button>
          )}
          
          {/* BlueSky Direct Post Button - Only for BlueSky posts when connected */}
          {canPostToBlueSky && !post.status?.includes('posted') && (
            <button 
              onClick={() => setShowBlueSkyConfirm(true)}
              disabled={isPostingToBlueSky}
              className="w-full px-3 py-2 text-xs font-medium text-white bg-sky-600 hover:bg-sky-500 rounded-md shadow-lg shadow-sky-900/20 flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {isPostingToBlueSky ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Cloud className="w-4 h-4" />
              )}
              Post to BlueSky
            </button>
          )}
          
          {/* Standard Action Buttons */}
          <div className="flex justify-between gap-3">
            <button 
              onClick={() => setIsPreviewOpen(true)} 
              data-testid="preview-btn"
              className="flex-1 px-3 py-2 text-xs font-medium text-brand-gray-400 bg-brand-gray-700/30 hover:bg-brand-gray-700 hover:text-white border border-brand-gray-600 rounded-md transition-all flex items-center justify-center gap-2"
            >
              <Eye className="w-3.5 h-3.5" /> Preview
            </button>
            <button 
              onClick={handleQuickShare} 
              data-testid="share-btn"
              className="flex-1 px-3 py-2 text-xs font-medium text-brand-gray-300 bg-brand-gray-700/50 hover:bg-brand-gray-700 hover:text-white border border-brand-gray-600 rounded-md transition-all flex items-center justify-center gap-2"
            >
              <Share2 className="w-3.5 h-3.5" /> Share
            </button>
            <button 
              onClick={() => onPublish(post)} 
              data-testid="wizard-btn"
              className="flex-1 px-3 py-2 text-xs font-medium text-black bg-brand-orange hover:bg-brand-orange/90 rounded-md shadow-lg shadow-brand-orange/20 flex items-center justify-center gap-2"
            >
              <Send className="w-3 h-3" /> Wizard
            </button>
          </div>
        </div>
      </div>

      {/* BlueSky Confirmation Dialog */}
      {showBlueSkyConfirm && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" onClick={() => setShowBlueSkyConfirm(false)}>
          <div className="bg-brand-gray-900 border border-brand-gray-700 rounded-xl w-full max-w-md p-6 shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-sky-600/20 rounded-full">
                <Cloud className="w-6 h-6 text-sky-400" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white">Post to BlueSky?</h3>
                <p className="text-sm text-brand-gray-400">This will publish immediately</p>
              </div>
            </div>
            
            <div className="bg-black/50 rounded-lg p-4 mb-4 max-h-40 overflow-y-auto">
              <p className="text-sm text-brand-gray-300 whitespace-pre-wrap">{post.content}</p>
              <div className="mt-2 flex flex-wrap gap-1">
                {getHashtags().map((tag, idx) => (
                  <span key={idx} className="text-xs text-sky-400">#{tag.replace('#', '')}</span>
                ))}
              </div>
            </div>
            
            {post.imageUrl && (
              <div className="mb-4">
                <p className="text-xs text-brand-gray-500 mb-2">Image will be attached:</p>
                <img src={post.imageUrl} alt="Post" className="w-24 h-24 object-cover rounded-lg border border-brand-gray-700" />
              </div>
            )}
            
            <div className="flex gap-3">
              <button
                onClick={() => setShowBlueSkyConfirm(false)}
                className="flex-1 px-4 py-2 bg-brand-gray-700 hover:bg-brand-gray-600 text-white rounded-lg text-sm font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handlePostToBlueSky}
                disabled={isPostingToBlueSky}
                className="flex-1 px-4 py-2 bg-sky-600 hover:bg-sky-500 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {isPostingToBlueSky ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
                Post Now
              </button>
            </div>
          </div>
        </div>
      )}

      {isPreviewOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" onClick={() => setIsPreviewOpen(false)}>
          <div className="bg-transparent w-full max-w-md" onClick={e => e.stopPropagation()}>
            <div className="flex justify-end mb-4">
              <button onClick={() => setIsPreviewOpen(false)} className="text-white bg-brand-gray-800 p-2 rounded-full hover:bg-brand-gray-700">
                <X className="w-6 h-6" />
              </button>
            </div>
            <PreviewCard post={post} logo={profile?.logo} businessName={profile?.name} />
          </div>
        </div>
      )}

      {/* Best Times to Post Modal */}
      {showBestTimes && getBestTimes(post.platform) && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => setShowBestTimes(false)}>
          <div className="bg-brand-gray-900 border border-brand-gray-700 rounded-xl w-full max-w-sm p-5 shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <CalendarClock className="w-5 h-5 text-brand-orange" />
                <span className="text-lg font-semibold text-white">Best Times for {post.platform}</span>
              </div>
              <button onClick={() => setShowBestTimes(false)} className="text-brand-gray-400 hover:text-white p-1">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="bg-black/50 rounded-lg p-3">
                <span className="text-xs text-brand-gray-500 uppercase tracking-wider">Best Days</span>
                <p className="text-white mt-1">{getBestTimes(post.platform).days.join(', ')}</p>
              </div>
              
              <div className="bg-black/50 rounded-lg p-3">
                <span className="text-xs text-brand-gray-500 uppercase tracking-wider">Best Times</span>
                <p className="text-white mt-1">{getBestTimes(post.platform).times.join(' & ')}</p>
              </div>
              
              <div className="bg-brand-orange/10 border border-brand-orange/30 rounded-lg p-3 flex items-center gap-2">
                <Zap className="w-5 h-5 text-brand-orange shrink-0" />
                <div>
                  <span className="text-xs text-brand-orange/70 uppercase tracking-wider">Peak Time</span>
                  <p className="text-brand-orange font-semibold">{getBestTimes(post.platform).peak}</p>
                </div>
              </div>
              
              <div className="bg-black/30 rounded-lg p-3 border border-brand-gray-700">
                <p className="text-sm text-brand-gray-400">
                  💡 {getBestTimes(post.platform).tip}
                </p>
              </div>
            </div>
            
            <button 
              onClick={() => setShowBestTimes(false)}
              className="w-full mt-4 px-4 py-2 bg-brand-gray-700 hover:bg-brand-gray-600 text-white rounded-lg text-sm font-medium"
            >
              Got it!
            </button>
          </div>
        </div>
      )}

      {/* Composio Publish Confirmation Modal */}
      {showComposioConfirm && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" onClick={() => setShowComposioConfirm(false)}>
          <div className="bg-brand-gray-900 border border-brand-gray-700 rounded-xl w-full max-w-md p-6 shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-4">
              <div className={`p-3 rounded-full ${
                platformLower === 'linkedin' ? 'bg-blue-600/20' :
                platformLower === 'facebook' ? 'bg-blue-500/20' :
                'bg-gradient-to-br from-purple-500/20 via-pink-500/20 to-orange-400/20'
              }`}>
                {platformLower === 'linkedin' && <Linkedin className="w-6 h-6 text-blue-600" />}
                {platformLower === 'facebook' && <Facebook className="w-6 h-6 text-blue-500" />}
                {platformLower === 'instagram' && <Instagram className="w-6 h-6 text-pink-500" />}
              </div>
              <div>
                <h3 className="text-lg font-bold text-white">Publish to {post.platform}?</h3>
                <p className="text-sm text-brand-gray-400">This will post immediately to your connected account</p>
              </div>
            </div>
            
            <div className="bg-black/50 rounded-lg p-4 mb-4 max-h-40 overflow-y-auto">
              <p className="text-sm text-brand-gray-300 whitespace-pre-wrap">{post.content}</p>
              <div className="mt-2 flex flex-wrap gap-1">
                {getHashtags().map((tag, idx) => (
                  <span key={idx} className="text-xs text-brand-orange">#{tag.replace('#', '')}</span>
                ))}
              </div>
            </div>
            
            {post.imageUrl && (
              <div className="mb-4">
                <p className="text-xs text-brand-gray-500 mb-2">Image will be attached:</p>
                <img src={post.imageUrl} alt="Post" className="w-24 h-24 object-cover rounded-lg border border-brand-gray-700" />
              </div>
            )}
            
            <div className="flex gap-3">
              <button
                onClick={() => setShowComposioConfirm(false)}
                className="flex-1 px-4 py-2 bg-brand-gray-700 hover:bg-brand-gray-600 text-white rounded-lg text-sm font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handlePublishComposio}
                disabled={isPublishingComposio}
                data-testid="confirm-publish-btn"
                className="flex-1 px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {isPublishingComposio ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
                Publish Now
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PostCard;
